import { parseJsonSafe } from "#app/utils/json";
import { authFetch } from "./auth.client";
import { getJSON } from "./storage.client";
import { type PlannerWorkflow } from "./types";

export type WorkflowSvcContext = {
  orgName?: string;
  teamName?: string;
  teamKey?: string;
};

const ORG_TEAM_STORAGE_NAMESPACE = "org_team";

/**
 * The caller may VIEW this operator but not change it — they are in marketplace
 * try mode (or moderator review) and have not onboarded it into their workspace.
 *
 * The workflow service answers these with `403 { kind: "onboarding-required" }`
 * rather than a 404, precisely so the UI can prompt onboarding instead of
 * showing a generic failure. Distinct error class so callers can branch without
 * string-matching a message.
 */
class OnboardingRequiredError extends Error {
  readonly kind = "onboarding-required" as const;

  constructor(message?: string) {
    super(
      message ??
        "Onboard this operator to your workspace to make changes. Try mode is read-only.",
    );
    this.name = "OnboardingRequiredError";
  }
}

export function isOnboardingRequiredError(
  err: unknown,
): err is OnboardingRequiredError {
  return (
    err instanceof OnboardingRequiredError ||
    (typeof err === "object" &&
      err !== null &&
      (err as { kind?: string }).kind === "onboarding-required")
  );
}

/**
 * Convert the service's onboarding-required response into a typed throw.
 *
 * Call BEFORE a wrapper's generic `!response.ok` handling, and only for writes —
 * a read never returns this kind. Returns without touching the body on any other
 * response, so the caller's existing error handling is unchanged.
 */
async function throwIfOnboardingRequired(response: Response): Promise<void> {
  if (response.status !== 403) return;
  try {
    // Clone: the caller still needs to read the body for its own error path.
    const data = (await response.clone().json()) as {
      kind?: string;
      error?: string;
      message?: string;
    };
    if (
      data?.kind === "onboarding-required" ||
      data?.error === "onboarding-required"
    ) {
      throw new OnboardingRequiredError(data.message);
    }
  } catch (err) {
    if (isOnboardingRequiredError(err)) throw err;
    // Non-JSON 403 — let the caller's normal handling report it.
  }
}

function hasExplicitWorkflowSvcContext(ctx?: WorkflowSvcContext) {
  return Boolean(
    ctx &&
    (ctx.orgName !== undefined ||
      ctx.teamName !== undefined ||
      ctx.teamKey !== undefined),
  );
}

function readWorkflowSvcContextFromStorage(): WorkflowSvcContext {
  // Personal context is represented by null selections; in that case we return empty headers.
  if (typeof window === "undefined") return {};

  const org = getJSON<{ name?: string }>("currentOrg", {
    namespace: ORG_TEAM_STORAGE_NAMESPACE,
    touch: false,
  });
  const team = getJSON<{ name?: string; prefix?: string }>("currentTeam", {
    namespace: ORG_TEAM_STORAGE_NAMESPACE,
    touch: false,
  });

  return {
    orgName: org?.name ?? undefined,
    teamName: team?.name ?? undefined,
    teamKey: team?.prefix ?? undefined,
  };
}

/**
 * Build org/team context headers for workflow microservice requests.
 *
 * - If explicit context is provided (any field set, even to undefined), we use it verbatim.
 * - Otherwise, we default to the current org/team selection stored in localStorage.
 */
export function getWorkflowServiceContextHeaders(
  ctx?: WorkflowSvcContext,
): Record<string, string> {
  const resolved = hasExplicitWorkflowSvcContext(ctx)
    ? (ctx ?? {})
    : readWorkflowSvcContextFromStorage();

  const orgName = resolved.orgName;
  const teamName = resolved.teamName;
  const teamKey = resolved.teamKey;

  const userTimezone = getUserTimezone();

  return {
    ...(orgName ? { "x-active-org": orgName } : {}),
    ...(teamName ? { "x-active-team": teamName } : {}),
    ...(teamKey ? { "x-teamKey": teamKey } : {}),
    ...(userTimezone ? { "x-user-timezone": userTimezone } : {}),
  };
}

/** Natural-language schedule inputs without an explicit zone default to UTC on the service. */
const SCHEDULE_TIMEZONE_PATTERN =
  /\b(UTC|GMT|IST|EST|PST|CST|MST|PDT|EDT|CDT|HST|AKST|AKDT|[+-]\d{1,2}(?::?\d{2})?\s*(?:hours?|h)?\b)/i;

const SCHEDULE_IANA_TIMEZONE_PATTERN =
  /\b(?:Africa|America|Antarctica|Arctic|Asia|Atlantic|Australia|Europe|Indian|Pacific)\/[A-Za-z0-9_+-]+\b/;

/** Append the browser IANA timezone when the user did not specify one. */
export function withScheduleTimezone(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return trimmed;
  if (
    SCHEDULE_TIMEZONE_PATTERN.test(trimmed) ||
    SCHEDULE_IANA_TIMEZONE_PATTERN.test(trimmed)
  ) {
    return trimmed;
  }
  const tz = getUserTimezone();
  return tz ? `${trimmed} (${tz})` : trimmed;
}

/** Browser IANA timezone, e.g. `Asia/Kolkata`. Sent on workflow-svc requests. */
function getUserTimezone(): string | undefined {
  return typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : undefined;
}

export async function getNextUpcomingRun(
  workflowId: string,
  options?: { maxAttempts?: number; delayMs?: number },
): Promise<Date | null> {
  const { maxAttempts = 4, delayMs = 750 } = options ?? {};
  const now = Date.now();
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const data = await getUpcomingRuns(workflowId);
    const next = (data.upcomingRuns ?? [])
      .map((runTime) => new Date(runTime))
      .filter((runDate) => runDate.getTime() > now)
      .sort((a, b) => a.getTime() - b.getTime())[0];
    if (next) return next;
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
}

/** Headers for external execute/stream examples (Operator Info, API details). */
export function buildWorkflowExecuteExampleHeaders(options: {
  bearerToken: string;
  ctx?: WorkflowSvcContext;
  source?: "v1" | "external";
}): Record<string, string> {
  return {
    Authorization: `Bearer ${options.bearerToken}`,
    "x-source": options.source ?? "v1",
    ...getWorkflowServiceContextHeaders(options.ctx),
  };
}

const CURL_HEADER_ORDER = [
  "Authorization",
  "x-source",
  "x-active-org",
  "x-active-team",
  "x-teamKey",
] as const;

/** `-H "Name: value"` lines for cURL, stable key order. */
export function formatCurlHeaderLines(headers: Record<string, string>): string {
  const keys = [
    ...CURL_HEADER_ORDER.filter((k) => k in headers),
    ...Object.keys(headers).filter(
      (k) =>
        !CURL_HEADER_ORDER.includes(k as (typeof CURL_HEADER_ORDER)[number]),
    ),
  ];
  return keys.map((k) => `-H "${k}: ${headers[k]}"`).join(" \\\n  ");
}

export async function getWorkflows(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    /**
     * Filter by project. A project UUID returns that project's workflows;
     * `"none"` returns only the unassigned ones, which is what the project
     * operator picker offers -- a workflow belongs to at most one project, so
     * listing assigned ones would offer a move the API answers with 409.
     */
    projectId?: string;
    orgName?: string;
    teamName?: string;
    teamKey?: string;
  } = {},
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const {
    page = 1,
    limit = 20,
    search,
    // Do not default sortBy — backend historically ignores it; callers that need
    // newest-first (e.g. My Operators) must pass sortBy/sortOrder explicitly.
    sortBy,
    sortOrder,
    projectId,
    orgName,
    teamName,
    teamKey,
  } = params;

  const url = new URL(`${workflowServiceUrl}/api/v1/workflows`);
  url.searchParams.set("page", page.toString());
  url.searchParams.set("limit", limit.toString());
  if (search && search.trim()) {
    url.searchParams.set("search", search.trim());
  }
  if (sortBy) url.searchParams.set("sortBy", sortBy);
  if (sortOrder) url.searchParams.set("sortOrder", sortOrder);
  if (projectId) url.searchParams.set("projectId", projectId);

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: {
      ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
    },
  });
  if (!response.ok) throw new Error("Failed to load agents");
  const data = (await response.json()) as any;
  return data;
}

type ChatThread = {
  id: string;
  title: string;
  description: string | null;
  workflowId: bigint | null;
  lastUpdatedAt: string;
  isTryMode?: boolean;
};

type GetChatsResponse = {
  threads: ChatThread[];
};

export async function getChats(params: { page?: number; limit?: number } = {}) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { page = 1, limit = 20 } = params;

  const url = new URL(`${workflowServiceUrl}/api/v1/chats`);
  url.searchParams.set("page", page.toString());
  url.searchParams.set("limit", limit.toString());

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: {
      ...getWorkflowServiceContextHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to load chats");
  const data = (await response.json()) as GetChatsResponse;
  return data;
}

type ChatMessagesPagination = {
  hasMore?: boolean;
  oldestCursor?: string | null;
  newestCursor?: string | null;
};

export type ListMessagesResponse = {
  messages: Array<{
    id: string;
    createdAt: string;
    role: string;
    type: string;
    content: string;
  }>;
  workflow?: PlannerWorkflow;
  pagination?: ChatMessagesPagination;
  workflowPublicId?: string | null;
  rule?: unknown | null;
  rulePublicId?: string | null;
  latestRun?: { id: string; status: string } | null;
  contextFiles?: Array<{
    id: string;
    name: string;
    objectKey: string;
    contentType?: string;
    sizeBytes?: number;
    createdAt: string;
  }>;
  isTryMode?: boolean;
  triesUsed?: number;
};

/** Cursor-based list: initial load `limit=100`; older `limit=20&before=`; newer `limit=20&after=`. */
export type GetChatMessagesParams = {
  limit?: number;
  before?: string;
  after?: string;
};

export async function getChatMessages(
  threadId: string,
  params: GetChatMessagesParams = {},
): Promise<ListMessagesResponse> {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;

  const url = new URL(
    `${workflowServiceUrl}/api/v1/chats/${threadId}/messages`,
  );

  const { before, after } = params;
  const limit =
    params.limit ?? (before !== undefined || after !== undefined ? 20 : 100);

  url.searchParams.set("limit", String(limit));
  if (before !== undefined && before !== "") {
    url.searchParams.set("before", before);
  } else if (after !== undefined && after !== "") {
    url.searchParams.set("after", after);
  }

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getWorkflowServiceContextHeaders(),
    },
  });

  if (!response.ok) {
    const error = (await response.json()) as { error?: string };
    throw new Error(error.error || "Failed to fetch messages");
  }

  const data = (await response.json()) as ListMessagesResponse;
  return data;
}

export async function getWorkflowById(
  workflowId: string,
  options?: { orgName?: string; teamName?: string; teamKey?: string },
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { orgName, teamName, teamKey } = options ?? {};
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}`,
    {
      method: "GET",
      headers: {
        ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to load workflow (${response.status})`);
  }
  const data = (await response.json()) as {
    workflow?: any;
    isPaused?: boolean;
    [key: string]: any;
  };
  // API may return workflow data directly or nested in 'workflow' property
  return data;
}

export type WorkflowVersionItem = {
  versionId: string;
  workflowId: string;
  versionNumber: number;
  isDefault: boolean;
  createdAt: string;
  // Keycloak `sub` of the publisher; resolve to a display name
  // out-of-band on the client (cache / Keycloak userinfo).
  createdBy: string;
  // Optional free-form commit-message annotation. Editable via
  // `updateWorkflowVersionCommitMessage`; null when never set or cleared.
  commitMessage: string | null;
};

export type ListWorkflowVersionsResponse = {
  versions: WorkflowVersionItem[];
  total: number;
  truncated: boolean;
};

export async function getWorkflowVersions(
  workflowId: string,
  params: { limit?: number; includeAll?: boolean } & WorkflowSvcContext = {},
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { limit = 100, includeAll = true, orgName, teamName, teamKey } = params;

  const url = new URL(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/versions`,
  );
  url.searchParams.set("limit", String(Math.min(100, Math.max(1, limit))));
  url.searchParams.set("includeAll", includeAll ? "true" : "false");

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: {
      ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load workflow versions (${response.status})`);
  }

  return (await response.json()) as ListWorkflowVersionsResponse;
}

export async function setWorkflowDefaultVersion(
  workflowId: string,
  versionId: string,
  options?: WorkflowSvcContext,
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { orgName, teamName, teamKey } = options ?? {};

  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/versions/${versionId}/default`,
    {
      method: "POST",
      headers: {
        ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
      },
    },
  );

  // Try-mode/moderator write: surface the onboarding prompt, not a generic error.
  await throwIfOnboardingRequired(response);
  if (!response.ok) {
    throw new Error(
      `Failed to set default workflow version (${response.status})`,
    );
  }

  return (await response.json()) as WorkflowVersionItem;
}

export type UpdateWorkflowVersionCommitMessageResponse = {
  versionId: string;
  versionNumber: number;
  commitMessage: string | null;
};

/**
 * Update (or clear, with `null`) the optional commit-message annotation on
 * a published workflow version.
 */
export async function updateWorkflowVersionCommitMessage(
  workflowId: string,
  versionId: string,
  commitMessage: string | null,
  options?: WorkflowSvcContext,
): Promise<UpdateWorkflowVersionCommitMessageResponse> {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { orgName, teamName, teamKey } = options ?? {};

  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/versions/${versionId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
      },
      body: JSON.stringify({ commitMessage }),
    },
  );

  // Try-mode/moderator write: surface the onboarding prompt, not a generic error.
  await throwIfOnboardingRequired(response);
  if (!response.ok) {
    throw new Error(
      `Failed to update workflow version commit message (${response.status})`,
    );
  }

  return (await response.json()) as UpdateWorkflowVersionCommitMessageResponse;
}

/**
 * What a run's model usage cost, as the workflow service reports it.
 *
 * `costMicros` is the authoritative figure — a decimal string, because it sums a
 * bigint column and can exceed what a JS number holds exactly, and this is
 * money. `costUsd` is for display only.
 *
 * `unpricedCalls > 0` means the total is a FLOOR: some calls had no determinable
 * cost, and every sum treats an unknown as zero because that is the only thing a
 * sum can do. A surface showing the figure must mark it rather than present it
 * as complete.
 */
export type RunCostWire = {
  costMicros: string;
  costUsd: number;
  llmCalls: number;
  unpricedCalls: number;
  /** Calls a gateway fallback answered, because the tier's own model could not. */
  fallbackCalls: number;
  totalTokens: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  /** True when the figure includes runs this run caused by calling operators. */
  rolledUp?: boolean;
  /** Caused runs left unpriced because the nesting depth cap was reached. */
  unresolvedSubruns?: number;
  /** The part of the run's own spend that wrote code. Absent when none was. */
  codeGeneration?: CodeGenerationCostWire;
};

/**
 * The part of a cost figure spent WRITING code: the coder agent's own model
 * calls, made the first time a step runs or after an edit clears its code.
 * The rest of the figure is what the code cost when it ran.
 */
export type CodeGenerationCostWire = {
  llmCalls: number;
  unpricedCalls: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costMicros: string;
  costUsd: number;
};

/** One activity of a run, and what it cost. */
export type StepCostWire = {
  activityRunId: string | null;
  stepId: string | null;
  /** Set when this step called another operator; the cost is that run's total. */
  subworkflowRunId?: string;
  llmCalls: number;
  unpricedCalls: number;
  fallbackCalls: number;
  totalTokens: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  costMicros: string;
  costUsd: number;
  /** The part of this step's figure spent writing its code. */
  codeGeneration?: CodeGenerationCostWire;
};

/**
 * One model call a run made, from the run's model-call listing.
 *
 * `costMicros`/`costUsd` are `null` when the gateway reported no cost, and for
 * a call served from its cache: neither is a free call, so neither may be drawn
 * as $0.00. `gatewayFallbacks` is `null` when it was not recorded, which is not
 * the same as 0.
 */
export type LlmCallWire = {
  callId: string;
  activityRunId: string | null;
  stepId: string | null;
  model: string;
  /** The tier the call was requested as; `null` when a model was named directly. */
  modelType: string | null;
  provider: string;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  totalTokens: number;
  costMicros: string | null;
  costUsd: number | null;
  servedFromCache: boolean;
  gatewayFallbacks: number | null;
  /** Whether the call wrote the step's code or was made by the code running. */
  phase: "code-generation" | "execution";
  agentRole?: string;
  finishReason?: string;
  createdAt: string;
};

/**
 * Every model call a run made directly, oldest first. Calls made by operators
 * the run called are listed under those runs. `truncated` means the run made
 * more calls than one read returns.
 */
export async function getRunLlmCalls(
  runId: string,
): Promise<{ calls: LlmCallWire[]; truncated: boolean }> {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflow-runs/${runId}/llm-calls`,
    {
      method: "GET",
      headers: {
        ...getWorkflowServiceContextHeaders(),
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to load model calls (${response.status})`);
  }
  return (await response.json()) as {
    calls: LlmCallWire[];
    truncated: boolean;
  };
}

/** All-time model spend for one operator. */
export type OperatorCostWire = {
  costMicros: string;
  costUsd: number;
  runs: number;
  completedRuns: number;
  failedRuns: number;
  measuredRuns: number;
  llmCalls: number;
  unpricedCalls: number;
  totalTokens: number;
  /**
   * Total spend divided by runs that completed — the price of one outcome.
   * `null` when nothing has completed, which is NOT zero.
   */
  avgCostPerCompletedRunUsd: number | null;
  avgCostPerRunUsd: number | null;
};

export async function getWorkflowRuns(
  workflowId: string,
  params: {
    page?: number;
    limit?: number;
    status?: string;
    searchQuery?: string;
  } = {},
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { page = 1, limit = 10, status, searchQuery } = params;

  // If searching by specific runId, use the direct endpoint
  if (searchQuery && searchQuery.trim()) {
    const url = `${workflowServiceUrl}/api/v1/workflow-runs/${searchQuery.trim()}`;
    const response = await authFetch(url, {
      method: "GET",
      headers: {
        ...getWorkflowServiceContextHeaders(),
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Return empty result if run not found
        return {
          workflowRuns: [],
          pagination: {
            page: 1,
            limit,
            total: 0,
            totalPages: 0,
          },
        };
      }
      throw new Error(`Failed to load workflow run (${response.status})`);
    }

    const data = (await response.json()) as {
      workflowRun: {
        id: string;
        workflowId: string;
        workflowName: string;
        status:
          | "scheduled"
          | "running"
          | "completed"
          | "failed"
          | "cancelled"
          | "waiting"
          | "queued"
          | "timeout";
        inputs: unknown;
        createdAt: string | Date;
        updatedAt: string | Date;
        // Searching by run id takes THIS path, not the list endpoint, so the
        // cost column would blank the moment somebody searched without it.
        cost: (RunCostWire & { perStep: StepCostWire[] }) | null;
      };
    };

    // Return as array with single item
    return {
      workflowRuns: [data.workflowRun],
      pagination: {
        page: 1,
        limit,
        total: 1,
        totalPages: 1,
      },
    };
  }

  // Regular list endpoint with pagination and filters
  const url = new URL(`${workflowServiceUrl}/api/v1/workflow-runs`);
  url.searchParams.set("workflowId", workflowId);
  url.searchParams.set("page", page.toString());
  url.searchParams.set("limit", limit.toString());

  if (status && status !== "all") {
    url.searchParams.set("status", status);
  }

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: {
      ...getWorkflowServiceContextHeaders(),
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to load workflow runs (${response.status})`);
  }
  const data = (await response.json()) as {
    workflowRuns: Array<{
      id: string;
      workflowId: string;
      workflowName: string;
      status:
        | "scheduled"
        | "running"
        | "completed"
        | "failed"
        | "cancelled"
        | "waiting"
        | "queued"
        | "timeout";
      inputs: unknown;
      createdAt: string | Date;
      updatedAt: string | Date;
      /** `null` means no recorded usage — unknown, NOT free. Never draw $0.00. */
      cost: RunCostWire | null;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  return data;
}

export async function getWorkflowRunById(runId: string) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflow-runs/${runId}`,
    {
      method: "GET",
      headers: {
        ...getWorkflowServiceContextHeaders(),
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to load workflow run (${response.status})`);
  }
  const data = (await response.json()) as {
    workflowRun: {
      id: string;
      workflowId: string;
      workflowName: string;
      workflowDescription: string | null;
      status:
        | "scheduled"
        | "running"
        | "completed"
        | "failed"
        | "cancelled"
        | "waiting"
        | "queued"
        | "timeout";
      inputs: unknown;
      createdAt: string | Date;
      updatedAt: string | Date;
      /**
       * The workflow version this run was pinned to. Absent for runs started
       * before versioning, and for draft (unversioned) runs.
       */
      version?: {
        versionId: string;
        versionNumber: number;
        isDefault: boolean;
        createdAt: string;
        createdBy: string;
        commitMessage: string;
      } | null;
      /**
       * What this run's model usage cost, with the per-activity split.
       *
       * Rolled up: it includes the runs this run caused by calling other
       * operators, and the calling step is priced at that work rather than at
       * zero. `null` when no usage was recorded.
       */
      cost: (RunCostWire & { perStep: StepCostWire[] }) | null;
    };
    // Snapshot of the workflow definition (version or draft) pinned to this
    // run. Surfaces the agent_prompt + code_cell that actually executed so
    // the run-detail page can show them on hover, even after the workflow
    // has been edited.
    workflowDefinition?: PlannerWorkflow | null;
    activityRuns: Array<{
      id: string;
      stepId: string;
      stepName: string;
      stepDescription: string | null;
      status:
        | "scheduled"
        | "running"
        | "completed"
        | "failed"
        | "cancelled"
        | "waiting"
        | "queued"
        | "timeout";
      output: unknown;
      createdAt: string | Date;
      updatedAt: string | Date;
      kind?: "step" | "condition";
      conditionId?: string;
      conditionName?: string;
      userForm?: {
        approved: boolean | null;
        values: Record<string, unknown>;
        fieldLabels: Record<string, string>;
        fieldTypes: Record<string, string>;
        reviewedAt: string | null;
      };
    }>;
  };
  return data;
}

export async function deleteWorkflow(workflowId: string) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
    },
  );
  if (!response.ok) {
    const error = (await response.json()) as { error?: string };
    throw new Error(
      error.error || `Failed to delete workflow (${response.status})`,
    );
  }
  const data = (await response.json()) as {
    success: boolean;
    message?: string;
  };
  return data;
}

export type WorkflowExportVersionMode = "all" | "default" | "explicit";

export type WorkflowExportRequestBody = {
  versionMode?: WorkflowExportVersionMode;
  versionIds?: string[];
};

export type WorkflowImportResponse = {
  rootWorkflowId: string;
  workflows: Array<{ sourceWorkflowId: string; workflowId: string }>;
  integrationsManifest: { services: string[]; envNames: string[] };
  /** Webhook triggers on the source workflow(s) that were NOT imported (webhook
   * triggers are not portable) — the UI prompts the user to re-attach them. */
  webhookTriggersNotImported?: number;
  /** Source → new id for every policy recreated from the bundle. Empty for
   * bundles without attached policies. */
  policies?: Array<{ sourceRuleId: string; ruleId: string }>;
  /** Attached policies the active org still lacks after import. Only populated
   * for legacy bundles (exported before policies were carried) — the UI warns
   * to re-attach or re-import with a newer bundle. */
  missingPolicyRefs?: Array<{ ruleId: string; version: number | null }>;
};

/** Full portability bundle (import body / export response). */
export type WorkflowPortabilityBundle = Record<string, unknown>;

export async function exportWorkflowBundle(
  workflowId: string,
  body: WorkflowExportRequestBody = {},
  options?: WorkflowSvcContext,
): Promise<WorkflowPortabilityBundle> {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { orgName, teamName, teamKey } = options ?? {};

  const payload: WorkflowExportRequestBody = {};
  if (body.versionMode === "explicit") {
    payload.versionMode = "explicit";
    const ids: WorkflowExportRequestBody["versionIds"] | string =
      body.versionIds;
    payload.versionIds = Array.isArray(ids) ? ids : ids ? [ids] : [];
  } else if (body.versionMode === "default") {
    payload.versionMode = "default";
  } else {
    payload.versionMode = body.versionMode ?? "all";
  }

  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/export`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const err = (await response.json()) as {
        error?: string;
        details?: string;
      };
      if (err.error && err.details) message = `${err.error}: ${err.details}`;
      else if (err.error) message = err.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  return (await response.json()) as WorkflowPortabilityBundle;
}

export async function importWorkflowBundle(
  bundle: unknown,
  options?: WorkflowSvcContext,
): Promise<WorkflowImportResponse> {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { orgName, teamName, teamKey } = options ?? {};

  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/import`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
      },
      body: JSON.stringify(bundle),
    },
  );

  if (!response.ok) {
    let message = `Import failed (${response.status})`;
    try {
      const err = (await response.json()) as {
        error?: string;
        details?: string;
      };
      if (err.error && err.details) message = `${err.error}: ${err.details}`;
      else if (err.error) message = err.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  return (await response.json()) as WorkflowImportResponse;
}

export async function deleteChat(chatId: string) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/chats/${chatId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
    },
  );
  if (!response.ok) {
    const error = (await response.json()) as { error?: string };
    throw new Error(
      error.error || `Failed to delete chat (${response.status})`,
    );
  }
  const data = (await response.json()) as {
    success: boolean;
    message?: string;
  };
  return data;
}

export type ChatRestorePoint = {
  messageId: string;
  checkpointId: string;
  createdAt: string;
};

export type ChatRestorePreview = {
  messagesRemoved: number;
  filesRemoved: number;
  draftChanged: boolean;
  triggersRemoved: Array<{
    id: string;
    provider: string;
    eventType: string;
    resourceLabel: string | null;
  }>;
  scheduleChange: "none" | "remove" | "recreate";
  publishRollback: {
    fromVersionId: string | null;
    toVersionId: string | null;
    supported: boolean;
  } | null;
  ownedByAnotherThread: boolean;
};

/** Thrown for 409 `published_after_checkpoint`, carrying the list to confirm. */
export class RestoreNeedsConfirmationError extends Error {
  preview: ChatRestorePreview;

  constructor(message: string, preview: ChatRestorePreview) {
    super(message);
    this.name = "RestoreNeedsConfirmationError";
    this.preview = preview;
  }
}

export async function getChatRestorePoints(
  threadId: string,
): Promise<ChatRestorePoint[]> {
  const response = await authFetch(
    `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/chats/${threadId}/restore-points`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
    },
  );
  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(error.error || "Failed to load restore points");
  }
  const data = (await response.json()) as { restorePoints: ChatRestorePoint[] };
  return data.restorePoints;
}

async function postRestore(
  threadId: string,
  body: Record<string, unknown>,
): Promise<Response> {
  return authFetch(
    `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/chats/${threadId}/restore`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
      body: JSON.stringify(body),
    },
  );
}

export async function previewChatRestore(
  threadId: string,
  messageId: string,
): Promise<ChatRestorePreview> {
  const response = await postRestore(threadId, {
    messageId,
    previewOnly: true,
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(error.error || "Failed to preview restore");
  }
  const data = (await response.json()) as { preview: ChatRestorePreview };
  return data.preview;
}

export async function restoreChatToMessage(
  threadId: string,
  messageId: string,
  options: {
    confirmPublishedRollback?: boolean;
    /** The preview's `publishRollback.fromVersionId`: refused if production moved since. */
    expectedPublishedVersionId?: string | null;
  } = {},
): Promise<{ draftId: string | null; restoredToMessageId: string }> {
  const response = await postRestore(threadId, {
    messageId,
    ...(options.confirmPublishedRollback
      ? { confirmPublishedRollback: true }
      : {}),
    ...(options.expectedPublishedVersionId !== undefined
      ? { expectedPublishedVersionId: options.expectedPublishedVersionId }
      : {}),
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
      preview?: ChatRestorePreview;
    };
    if (
      (error.code === "published_after_checkpoint" ||
        error.code === "published_version_changed") &&
      error.preview
    ) {
      throw new RestoreNeedsConfirmationError(
        error.error || "Confirmation required",
        error.preview,
      );
    }
    throw new Error(error.error || "Failed to restore chat");
  }
  return response.json() as Promise<{
    draftId: string | null;
    restoredToMessageId: string;
  }>;
}

export async function deleteContextFile(threadId: string, fileId: string) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/chats/${threadId}/context-files/${fileId}`,
    {
      method: "DELETE",
      headers: {
        ...getWorkflowServiceContextHeaders(),
      },
    },
  );
  if (!response.ok) {
    const error = (await response.json()) as { error?: string };
    throw new Error(
      error.error || `Failed to delete context file (${response.status})`,
    );
  }
  return (await response.json()) as { success: boolean };
}

export async function updateScheduleState(workflowId: string, paused: boolean) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/schedule`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
      body: JSON.stringify({ paused }),
    },
  );
  // Try-mode/moderator write: surface the onboarding prompt, not a generic error.
  await throwIfOnboardingRequired(response);
  if (!response.ok) {
    const error = (await response.json()) as { error?: string };
    throw new Error(
      error.error || `Failed to update schedule state (${response.status})`,
    );
  }
  const data = (await response.json()) as {
    success: boolean;
    message?: string;
  };
  return data;
}

export async function deleteSchedule(workflowId: string) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  // Use /schedule endpoint (consistent with updateScheduleState endpoint)
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/schedule`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
    },
  );

  // Try-mode/moderator write: surface the onboarding prompt, not a generic error.
  await throwIfOnboardingRequired(response);
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to delete schedule (${response.status})`;
    try {
      const error = JSON.parse(errorText) as {
        error?: string;
        message?: string;
      };
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      // If response is not JSON, use the text or default message
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  const data = (await response.json()) as {
    success: boolean;
    message?: string;
  };
  return data;
}

export async function parseSchedule(workflowId: string, input: string) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const timezone = getUserTimezone();
  const normalizedInput = withScheduleTimezone(input);
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/schedule/parse`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
      body: JSON.stringify({
        input: normalizedInput,
        ...(timezone ? { timezone } : {}),
      }),
    },
  );

  // Try-mode/moderator write: surface the onboarding prompt, not a generic error.
  await throwIfOnboardingRequired(response);
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to parse schedule (${response.status})`;
    try {
      const error = JSON.parse(errorText) as {
        error?: string;
        message?: string;
        details?: string;
      };
      // Combine error and details if both exist
      if (error.details) {
        errorMessage = error.details;
      } else if (error.error) {
        errorMessage = error.error;
        if (error.message && error.message !== error.error) {
          errorMessage += `: ${error.message}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
    } catch {
      // If response is not JSON, use the text or default message
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  const data = (await response.json()) as any;
  return data;
}

export async function getUpcomingRuns(
  workflowId: string,
  options?: { orgName?: string; teamName?: string; teamKey?: string },
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { orgName, teamName, teamKey } = options ?? {};
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/upcoming-runs`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to load upcoming runs (${response.status})`;
    try {
      const error = JSON.parse(errorText) as {
        error?: string;
        message?: string;
      };
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as {
    upcomingRuns: string[];
  };
  return data;
}

type ScheduleListItem = {
  id: string;
  workflowId: string;
  workflowName: string;
  workflowDescription?: string;
  scheduleDescription: string;
  isPaused: boolean;
  createdAt: string;
  updatedAt: string;
  scheduledTime?: string;
};

type GetSchedulesResponse = {
  schedules: ScheduleListItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

/**
 * Client-side fetch of schedules list (uses authFetch).
 * Use to resolve scheduleId from workflowId when needed.
 */
async function getSchedulesClient(
  params: {
    page?: number;
    limit?: number;
    orgName?: string;
    teamName?: string;
    teamKey?: string;
  } = {},
): Promise<GetSchedulesResponse> {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { page = 1, limit = 20, orgName, teamName, teamKey } = params;
  const url = new URL(`${workflowServiceUrl}/api/v1/schedules`);
  url.searchParams.set("page", page.toString());
  url.searchParams.set("limit", limit.toString());

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: {
      ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to load schedules: ${response.statusText}`);
  }
  return (await response.json()) as GetSchedulesResponse;
}

/**
 * Resolve schedule ID for a workflow (when it has an active schedule).
 */
export async function getScheduleIdForWorkflow(
  workflowId: string,
  options?: WorkflowSvcContext,
): Promise<string | null> {
  const data = await getSchedulesClient({
    page: 1,
    limit: 100,
    ...options,
  });
  const found = data.schedules?.find((s) => s.workflowId === workflowId);
  return found?.id ?? null;
}

export type UpdateScheduleWorkflowParamsBody = {
  inputs?: Record<string, unknown>;
  envs?: Record<string, string>;
  /**
   * Allow the executor to auto-retry a failed step on scheduled runs. Omit to
   * leave the schedule's current setting untouched; the server treats an
   * absent value as "keep as is" and an absent stored value as enabled.
   */
  autoRetryAllowed?: boolean;
};

/**
 * Update a schedule's workflow params (inputs and/or envs).
 * PUT /api/v1/schedules/:scheduleId/workflow-params
 *
 * Accepts either JSON body or FormData. When body is FormData, request is sent
 * as multipart/form-data (e.g. inputs[report_type]=weekly, inputs[data_file]=@file, envs[API_KEY]=xxx).
 */
export async function updateScheduleWorkflowParams(
  scheduleId: string,
  body: UpdateScheduleWorkflowParamsBody | FormData,
  options?: WorkflowSvcContext,
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const url = `${workflowServiceUrl}/api/v1/schedules/${scheduleId}/workflow-params`;
  const headers = getWorkflowServiceContextHeaders(options);

  const init: RequestInit = {
    method: "PUT",
    headers,
  };

  if (body instanceof FormData) {
    // Do not set Content-Type; browser sets multipart/form-data with boundary
    init.body = body;
  } else {
    (init.headers as Record<string, string>)["Content-Type"] =
      "application/json";
    init.body = JSON.stringify({
      inputs: body.inputs ?? {},
      envs: body.envs ?? {},
      ...(typeof body.autoRetryAllowed !== "undefined"
        ? { autoRetryAllowed: body.autoRetryAllowed }
        : {}),
    });
  }

  const response = await authFetch(url, init);

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to update schedule workflow params (${response.status})`;
    try {
      const error = JSON.parse(errorText) as {
        error?: string;
        message?: string;
      };
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as {
    success: boolean;
    message?: string;
  };
  return data;
}

/**
 * Fetch a schedule's workflow params (inputs, envs, integration_overrides,
 * autoRetryAllowed).
 * GET /api/v1/schedules/:scheduleId/args
 */
export async function getScheduleWorkflowParams(
  scheduleId: string,
  options?: WorkflowSvcContext,
): Promise<{
  inputs: any[];
  envs: any[];
  integration_overrides?: Record<string, string>;
  /**
   * Left `undefined` when the schedule has never been configured — callers
   * need to tell that apart from an explicit `false`.
   */
  autoRetryAllowed?: boolean;
}> {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const url = `${workflowServiceUrl}/api/v1/schedules/${scheduleId}/args`;
  const headers = getWorkflowServiceContextHeaders(options);

  const response = await authFetch(url, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to fetch schedule args (${response.status})`;
    try {
      const error = JSON.parse(errorText) as {
        error?: string;
        message?: string;
      };
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as {
    args: {
      inputs?: any[];
      envs?: any[];
      integration_overrides?: Record<string, string>;
      autoRetryAllowed?: boolean;
    } | null;
  };

  return {
    inputs: data.args?.inputs ?? [],
    envs: data.args?.envs ?? [],
    integration_overrides: data.args?.integration_overrides ?? {},
    autoRetryAllowed: data.args?.autoRetryAllowed,
  };
}

/**
 * Which envs the operator has a saved value for.
 * GET /api/v1/workflows/:workflowId/envs
 *
 * Returns presence only, never the values. Callers use this to show whether a
 * field will inherit a saved value; nothing needs the secret itself, and the
 * schedule form deliberately does not pre-fill from it (a blank field there
 * means "inherit", so pre-filling would turn a no-op save into an override).
 */
export async function getWorkflowSavedEnvNames(
  workflowId: string,
  options?: WorkflowSvcContext,
): Promise<Set<string>> {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const url = `${workflowServiceUrl}/api/v1/workflows/${workflowId}/envs`;
  const headers = getWorkflowServiceContextHeaders(options);

  const response = await authFetch(url, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch workflow envs (${response.status})`);
  }

  const data = (await response.json()) as {
    envs?: Array<{ name?: string; value?: string | null }>;
  };

  return new Set(
    (data.envs ?? [])
      .filter((env) => env.name && (env.value ?? "").trim() !== "")
      .map((env) => env.name as string),
  );
}

export async function cancelWorkflowRuns(params: {
  runIds?: string[];
  workflowId?: string;
  reason?: string;
}) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { runIds, workflowId, reason } = params;

  // Validate: must provide either workflowId or at least one runId
  if (!workflowId && (!runIds || runIds.length === 0)) {
    throw new Error("Either workflowId or at least one runId must be provided");
  }

  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflow-runs/cancel`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
      body: JSON.stringify({
        ...(runIds && runIds.length > 0 ? { runIds } : {}),
        ...(workflowId ? { workflowId } : {}),
        ...(reason ? { reason } : {}),
      }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to cancel workflow runs (${response.status})`;
    try {
      const error = JSON.parse(errorText) as {
        error?: string;
        message?: string;
      };
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as {
    success: boolean;
    message?: string;
  };
  return data;
}

export async function getWorkflowDashboard(workflowId: string) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflow-runs/dashboard/${workflowId}`,
    {
      method: "GET",
      headers: {
        ...getWorkflowServiceContextHeaders(),
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to load workflow dashboard (${response.status})`);
  }

  const data = (await response.json()) as {
    counts: {
      scheduled: number;
      running: number;
      completed: number;
      failed: number;
      cancelled: number;
      waiting: number;
      queued: number;
      timeout: number;
    };
    /** `null` when the operator has never run: show nothing, not zeroes. */
    cost: OperatorCostWire | null;
  };
  return data;
}

export async function updateWorkflowVisibility(
  workflowId: string,
  options: {
    targetScope: "org" | "team";
    shareWithTeamKeys?: string[];
    orgName?: string;
    teamName?: string;
    teamKey?: string;
  },
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { targetScope, shareWithTeamKeys, orgName, teamName, teamKey } =
    options;
  const headers = {
    "Content-Type": "application/json",
    ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
  };
  const url = `${workflowServiceUrl}/api/v1/workflows/${workflowId}/sharing`;
  const body = JSON.stringify({
    targetScope,
    shareWithTeamKeys: shareWithTeamKeys ?? [],
  });

  const response = await authFetch(url, {
    method: "PATCH",
    headers,
    body,
  });

  if (!response.ok) {
    const error =
      (await response.text()) ||
      `Failed to update workflow sharing (${response.status})`;
    throw new Error(error);
  }
  const data = (await response.json()) as {
    sharedTeamKeys: string[];
    targetScope: "org" | "team";
  };
  return data;
}

export async function createThreadFromWorkflow(workflowId: string) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/chats/from-workflow`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
      body: JSON.stringify({ workflowId }),
    },
  );
  if (!response.ok) {
    const error = (await response.json()) as { error?: string };
    throw new Error(
      error.error ||
        `Failed to create thread from workflow (${response.status})`,
    );
  }
  const data = (await response.json()) as {
    threadId: string;
  };
  return data;
}

/**
 * Create a chat thread for marketplace "Try" mode.
 * Passes `versionPublicId` to bypass RBAC ownership checks — the user
 * does not own the source workflow but is allowed to try it via the
 * published listing.
 */
export async function createThreadFromWorkflowForTry(options: {
  workflowId: string;
  versionPublicId: string;
  title?: string;
}) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/chats/from-workflow`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getWorkflowServiceContextHeaders(),
      },
      body: JSON.stringify({
        workflowId: options.workflowId,
        versionPublicId: options.versionPublicId,
        title: options.title,
        isTryMode: true,
      }),
    },
  );
  if (!response.ok) {
    const error = (await response.json()) as { error?: string };
    throw new Error(
      error.error ||
        `Failed to create marketplace try thread (${response.status})`,
    );
  }
  const data = (await response.json()) as {
    threadId: string;
  };
  return data;
}

export async function getWorkflowThreads(
  workflowId: string,
  isTryMode?: boolean,
  limit?: number,
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  let url = `${workflowServiceUrl}/api/v1/chats?workflowId=${workflowId}`;
  if (isTryMode !== undefined) {
    url += `&isTryMode=${isTryMode}`;
  }
  if (limit !== undefined) {
    url += `&limit=${limit}`;
  }
  const response = await authFetch(url, {
    method: "GET",
    headers: {
      ...getWorkflowServiceContextHeaders(),
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return { threads: [] };
    }
    const errorText = await response.text();
    let errorMessage = `Failed to get workflow threads (${response.status})`;
    try {
      const error = JSON.parse(errorText) as {
        error?: string;
        message?: string;
      };
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as {
    threads: Array<{
      id: string;
      title: string;
      description: string | null;
      workflowId: string;
      rule: string | null;
      lastUpdatedAt: string;
      sharedTeamKeys: string[];
      isTryMode?: boolean;
      hasMessages?: boolean;
    }>;
  };
  return data;
}

// ─── Event Trigger API helpers ─────────────────────────────────────────────────

export type TriggerExecutionArgs = {
  envs?: Array<{ name: string; description: string; value?: string | null }>;
  integration_overrides?: Record<string, string>;
};

export type EventTrigger = {
  id: string;
  provider: string;
  eventType: string;
  catalogKey?: string | null;
  resourceId: string | null;
  /**
   * Human-readable name for `resourceId`, captured from the resource picker at
   * attach time (e.g. "#deploys" for a Slack channel id). Display only — never
   * used for matching. Null for legacy rows, hand-typed resources, and servers
   * that predate the column, so always fall back to `resourceId`.
   */
  resourceLabel?: string | null;
  fieldMapping: Record<string, string> | null;
  staticInputs?: Record<string, unknown> | null;
  executionArgs?: TriggerExecutionArgs | null;
  connectedAccountId: string | null;
  isActive: boolean;
  /**
   * Provider subscription lifecycle. An OPEN string set — `active`,
   * `awaiting_first_event`, `expired`, `failed`, `pending` come from the
   * subscription row; `unregistered` (no subscription at all) and `unknown`
   * (lookup failed) are synthesized by the workflow service. Never switch
   * exhaustively; treat anything unrecognized — including null/absent — as
   * "not confirmed healthy". Current servers never send `null` (they
   * synthesize `unregistered`/`unknown` instead), but the type stays
   * null-tolerant so this client accepts BOTH forms across deploy skew
   * (an older server still emits `null`). Only compare with `===` against
   * known values; never assume the string methods exist.
   */
  status?: string | null;
  expiresAt?: string | null;
  testedAt: string | null;
  createdAt: string;
};

export type PreviewMappingResult = {
  matched: Array<{
    workflowInput: string;
    enrichedField: string;
    confidence: "exact" | "fuzzy";
  }>;
  unmatched: string[];
  extraFields: string[];
  schema: Record<string, { description: string; type: string }>;
  sample: Record<string, unknown>;
};

/**
 * GET /api/v1/workflows/:workflowId/triggers
 * List all event triggers attached to a workflow.
 */
export async function getEventTriggers(
  workflowId: string,
  ctx?: WorkflowSvcContext,
): Promise<{ triggers: EventTrigger[] }> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers`;
  const response = await authFetch(url, {
    headers: getWorkflowServiceContextHeaders(ctx),
  });
  if (!response.ok) throw new Error("Failed to fetch event triggers");
  return response.json() as Promise<{ triggers: EventTrigger[] }>;
}

/**
 * GET /api/v1/event-triggers
 * List event triggers for the current org. Offset-paginated like operators
 * (`page` / `limit` / `pagination.total`), with optional `search`.
 */
export async function getAllEventTriggers(
  ctx?: WorkflowSvcContext,
  params?: { page?: number; limit?: number; search?: string },
): Promise<{
  triggers: (EventTrigger & { workflowId: string; workflowName: string })[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}> {
  const url = new URL(`${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/event-triggers`);
  if (params?.page != null) url.searchParams.set("page", String(params.page));
  if (params?.limit != null)
    url.searchParams.set("limit", String(params.limit));
  if (params?.search?.trim()) {
    url.searchParams.set("search", params.search.trim());
  }

  const response = await authFetch(url.toString(), {
    headers: getWorkflowServiceContextHeaders(ctx),
  });
  if (!response.ok) throw new Error("Failed to fetch all event triggers");
  return response.json() as Promise<{
    triggers: (EventTrigger & { workflowId: string; workflowName: string })[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>;
}

/**
 * A same-trigger conflict from POST /triggers (409). Carries the server-matched
 * existing trigger id so callers can recover by updating it in place — the
 * server matches on the CANONICAL resource id (Slack team id, Gmail "global",
 * canonicalized GitHub repo, …) which the client cannot compute itself.
 */
export class TriggerConflictError extends Error {
  existingTriggerId?: string;
  constructor(message: string, existingTriggerId?: string) {
    super(message);
    this.name = "TriggerConflictError";
    this.existingTriggerId = existingTriggerId;
  }
}

/** Error carrying the server's machine-readable reason, for inline field messages. */
class TriggerInputUploadError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = "TriggerInputUploadError";
    this.code = code;
  }
}

/**
 * POST /api/v1/workflows/:workflowId/triggers/upload-input
 *
 * Upload a file to use as the static value of a `file` / `image` workflow input
 * when attaching a webhook trigger. Returns a durable object URL — a file input's
 * value is a plain URL string everywhere on the platform, so the caller just
 * stores this in `staticInputs[name]`.
 */
export async function uploadTriggerInputFile(
  workflowId: string,
  file: File,
  opts?: {
    inputName?: string;
    allowedExtensions?: string[];
    maxFileSize?: number;
  },
  ctx?: WorkflowSvcContext,
): Promise<{ name: string; objectKey: string; url: string }> {
  const form = new FormData();
  form.append("file", file);
  if (opts?.inputName) form.append("inputName", opts.inputName);
  if (opts?.allowedExtensions?.length) {
    form.append("allowedExtensions", JSON.stringify(opts.allowedExtensions));
  }
  if (opts?.maxFileSize) form.append("maxFileSize", String(opts.maxFileSize));

  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/upload-input`;
  // No Content-Type header: the browser must set the multipart boundary itself.
  const response = await authFetch(url, {
    method: "POST",
    headers: getWorkflowServiceContextHeaders(ctx),
    body: form,
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
    };
    throw new TriggerInputUploadError(
      err.error ?? "Could not upload the file",
      err.code,
    );
  }
  return response.json() as Promise<{
    name: string;
    objectKey: string;
    url: string;
  }>;
}

/**
 * POST /api/v1/workflows/:workflowId/triggers
 * Attach a new webhook trigger. With `upsert: true` the server updates the
 * matching existing trigger's config in place (200, `updated: true`) instead
 * of returning 409 — the attach-vs-update decision is server-side because
 * only registration knows the canonical resource id.
 */
export async function attachEventTrigger(
  workflowId: string,
  payload: {
    provider: string;
    eventType: string;
    catalogKey?: string;
    connectedAccountId: string;
    resourceId?: string;
    /**
     * Display name of the picked resource. Cosmetic only — the server stores it
     * verbatim and never matches on it, so registration canonicalizing
     * `resourceId` leaves it valid. Omit for hand-typed ids.
     */
    resourceLabel?: string;
    fieldMapping?: Record<string, string>;
    staticInputs?: Record<string, unknown>;
    executionArgs?: TriggerExecutionArgs;
    upsert?: boolean;
    /** Chat that attached this, so restoring that chat can take it down again. */
    sourceThreadId?: string;
  },
  ctx?: WorkflowSvcContext,
): Promise<{
  trigger: EventTrigger;
  message: string;
  webhookRegistered?: boolean;
  updated?: boolean;
  rebound?: boolean;
  requiresRetest?: boolean;
}> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers`;
  const response = await authFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getWorkflowServiceContextHeaders(ctx),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as {
      error?: string;
      existingTriggerId?: string;
    };
    const message = err.error ?? "Failed to attach event trigger";
    if (response.status === 409 && err.existingTriggerId) {
      throw new TriggerConflictError(message, err.existingTriggerId);
    }
    throw new Error(message);
  }
  return response.json() as Promise<{
    trigger: EventTrigger;
    message: string;
    webhookRegistered?: boolean;
    updated?: boolean;
    rebound?: boolean;
    requiresRetest?: boolean;
  }>;
}

/**
 * PUT /api/v1/workflows/:workflowId/triggers/:triggerId
 * Update an existing trigger's input configuration (fieldMapping / staticInputs
 * / executionArgs) after it was saved — like editing a schedule's inputs.
 * Changing the input config re-arms the test gate unless keepTested is set.
 */
export async function updateEventTrigger(
  workflowId: string,
  triggerId: string,
  payload: {
    fieldMapping?: Record<string, string> | null;
    staticInputs?: Record<string, unknown> | null;
    executionArgs?: TriggerExecutionArgs | null;
    keepTested?: boolean;
  },
  ctx?: WorkflowSvcContext,
): Promise<{ success: boolean; requiresRetest?: boolean }> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}`;
  const response = await authFetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getWorkflowServiceContextHeaders(ctx),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? "Failed to update event trigger");
  }
  return response.json() as Promise<{
    success: boolean;
    requiresRetest?: boolean;
  }>;
}

/**
 * DELETE /api/v1/workflows/:workflowId/triggers/:triggerId
 */
export async function deleteEventTrigger(
  workflowId: string,
  triggerId: string,
  ctx?: WorkflowSvcContext,
): Promise<void> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}`;
  const response = await authFetch(url, {
    method: "DELETE",
    headers: getWorkflowServiceContextHeaders(ctx),
  });
  if (!response.ok) throw new Error("Failed to delete event trigger");
}

/**
 * PATCH /api/v1/workflows/:workflowId/triggers/:triggerId/pause
 */
export async function pauseEventTrigger(
  workflowId: string,
  triggerId: string,
  isPaused: boolean,
  ctx?: WorkflowSvcContext,
): Promise<void> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}/pause`;
  const response = await authFetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getWorkflowServiceContextHeaders(ctx),
    },
    body: JSON.stringify({ isPaused }),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? "Failed to update trigger state");
  }
}

/**
 * POST /api/v1/workflows/:workflowId/triggers/:triggerId/re-register
 * Repair a trigger whose provider subscription is missing or dead. Keeps the
 * trigger id and `testedAt`, so a previously-tested trigger stays live instead
 * of dropping events as `dropped_untested` the way delete-and-re-attach would.
 */
export async function reRegisterEventTrigger(
  workflowId: string,
  triggerId: string,
  ctx?: WorkflowSvcContext,
): Promise<{
  subscriptionId: string;
  resourceId: string | null;
  replaced: boolean;
  message: string;
}> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}/re-register`;
  const response = await authFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getWorkflowServiceContextHeaders(ctx),
    },
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? "Failed to re-register webhook");
  }
  return (await response.json()) as {
    subscriptionId: string;
    resourceId: string | null;
    replaced: boolean;
    message: string;
  };
}

/**
 * GET /api/v1/workflows/:workflowId/triggers/:triggerId/url
 * Get the webhook URL for custom webhooks.
 */
export async function getWebhookUrl(
  workflowId: string,
  triggerId: string,
  ctx?: WorkflowSvcContext,
): Promise<{
  url: string;
  token?: string;
  tokenRevealed: boolean;
  expiresAt: string | null;
}> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}/url`;
  const response = await authFetch(url, {
    method: "GET",
    headers: getWorkflowServiceContextHeaders(ctx),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? "Failed to get webhook URL");
  }
  return response.json() as Promise<{
    url: string;
    token?: string;
    tokenRevealed: boolean;
    expiresAt: string | null;
  }>;
}

/**
 * POST /api/v1/workflows/:workflowId/triggers/:triggerId/rotate-secret
 * Rotate the secret for a custom webhook. Returns the new token (one-time reveal).
 */
export async function rotateWebhookSecret(
  workflowId: string,
  triggerId: string,
  ctx?: WorkflowSvcContext,
): Promise<{
  url: string;
  token: string;
  rotatedAt: string;
}> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}/rotate-secret`;
  const response = await authFetch(url, {
    method: "POST",
    headers: getWorkflowServiceContextHeaders(ctx),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? "Failed to rotate webhook secret");
  }
  return response.json() as Promise<{
    url: string;
    token: string;
    rotatedAt: string;
  }>;
}

export type WebhookDelivery = {
  id: string;
  provider: string;
  eventType: string;
  deliveryId?: string;
  status: string;
  dropReason?: string;
  payloadSizeBytes?: number;
  temporalWorkflowId?: string;
  matchedTriggerIds?: string[];
  errorMessage?: string;
  rawPayloadTruncated?: unknown;
  enrichedDataTruncated?: unknown;
  receivedAt: string;
  processedAt?: string;
  /**
   * Whether the trigger the list was fetched for actually fired on this
   * delivery. The list is scoped server-side to rows that fired this trigger
   * OR matched no trigger at all (diagnostics), so `false` means "arrived but
   * did not run this workflow".
   */
  firedThisTrigger?: boolean;
  /** Delivery fanned out to more than one trigger (shared subscription). */
  sharedTrigger?: boolean;
};

/**
 * Why a trigger has no delivery stream to read. The request still succeeds —
 * this is the answer to "why did my trigger stop firing?", not an error.
 *
 * - `disconnected`: the connected account was disconnected, which pauses the
 *   trigger and detaches its subscription.
 * - `unregistered`: no provider subscription exists (attach-time registration
 *   returned none, or unpublish detached it and the republish never
 *   re-registered).
 */
export type WebhookDeliveriesUnavailableReason =
  | "disconnected"
  | "unregistered";

/**
 * GET /api/v1/workflows/:workflowId/triggers/:triggerId/deliveries
 * Get delivery history for a trigger.
 */
export async function getWebhookDeliveries(
  workflowId: string,
  triggerId: string,
  params?: { status?: string; cursor?: string; limit?: number },
  ctx?: WorkflowSvcContext,
): Promise<{
  deliveries: WebhookDelivery[];
  nextCursor?: string;
  unavailable?: WebhookDeliveriesUnavailableReason;
}> {
  const url = new URL(
    `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}/deliveries`,
  );
  if (params?.status) url.searchParams.set("status", params.status);
  if (params?.cursor) url.searchParams.set("cursor", params.cursor);
  if (params?.limit) url.searchParams.set("limit", String(params.limit));

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: getWorkflowServiceContextHeaders(ctx),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? "Failed to fetch delivery history");
  }
  return response.json() as Promise<{
    deliveries: WebhookDelivery[];
    nextCursor?: string;
    unavailable?: WebhookDeliveriesUnavailableReason;
  }>;
}

export type WebhookDeliveryRunLink = {
  triggerId: string;
  workflowId: string;
  runId: string;
  status: string;
};

export type WebhookDeliveryDetail = WebhookDelivery & {
  /** Full (untruncated) request payload as received from the provider. */
  rawPayload?: unknown;
  /** Full enriched data bag produced by the provider enrichment step. */
  enrichedData?: unknown;
  /** Run(s) THIS trigger fanned out to (scoped to the viewed workflow). */
  runs?: WebhookDeliveryRunLink[];
  /** Whether the trigger being viewed actually fired for this delivery. */
  firedThisTrigger?: boolean;
  /** Whether this delivery's subscription is shared by multiple workflows. */
  sharedTrigger?: boolean;
};

/**
 * GET /api/v1/workflows/:workflowId/triggers/:triggerId/deliveries/:deliveryId
 * Full single-delivery detail (untruncated payloads + resolved workflow runs).
 */
export async function getWebhookDeliveryDetail(
  workflowId: string,
  triggerId: string,
  deliveryId: string,
  ctx?: WorkflowSvcContext,
): Promise<WebhookDeliveryDetail> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}/deliveries/${deliveryId}`;
  const response = await authFetch(url, {
    method: "GET",
    headers: getWorkflowServiceContextHeaders(ctx),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? "Failed to fetch delivery detail");
  }
  return response.json() as Promise<WebhookDeliveryDetail>;
}

/**
 * GET /api/v1/workflows/:workflowId/triggers/providers/:provider/resources/:resource
 * List provider resources for dynamic dropdowns in the trigger config UI.
 */
export async function getProviderResources(
  workflowId: string,
  provider: string,
  resource: string,
  connectedAccountId?: string,
  options?: {
    q?: string;
    cursor?: string;
    parentId?: string;
    limit?: number;
  },
  ctx?: WorkflowSvcContext,
): Promise<{
  options: Array<{ label: string; value: string }>;
  nextCursor?: string;
}> {
  const url = new URL(
    `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/providers/${provider}/resources/${resource}`,
  );
  if (connectedAccountId) {
    url.searchParams.set("connectedAccountId", connectedAccountId);
  }
  if (options?.q) url.searchParams.set("q", options.q);
  if (options?.cursor) url.searchParams.set("cursor", options.cursor);
  if (options?.parentId) url.searchParams.set("parentId", options.parentId);
  if (options?.limit != null)
    url.searchParams.set("limit", String(options.limit));

  const response = await authFetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getWorkflowServiceContextHeaders(ctx),
    },
  });
  if (!response.ok) {
    let message = "Failed to fetch provider resources";
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // ignore parse failure
    }
    throw new Error(message);
  }
  return response.json() as Promise<{
    options: Array<{ label: string; value: string }>;
    nextCursor?: string;
  }>;
}

/**
 * POST /api/v1/workflows/:workflowId/triggers/preview-mapping
 * Returns auto-matched input <-> enriched field pairs.
 */
export async function previewTriggerMapping(
  workflowId: string,
  payload: {
    provider: string;
    eventType: string;
    catalogKey?: string;
    workflowInputNames: string[];
  },
  ctx?: WorkflowSvcContext,
): Promise<PreviewMappingResult> {
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/preview-mapping`;
  const response = await authFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getWorkflowServiceContextHeaders(ctx),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error("Failed to preview trigger mapping");
  return response.json() as Promise<PreviewMappingResult>;
}

/**
 * SSE endpoint: GET /api/v1/workflows/:workflowId/triggers/:triggerId/listen
 * Opens an EventSource to listen for real webhook events.
 * Returns an object with:
 * - eventSource: the EventSource instance (caller must close it)
 * - onEvent: register a callback for captured events
 * - onTimeout: register a callback for timeout
 * - close: cleanup function
 */
export function listenForTriggerEvent(
  workflowId: string,
  triggerId: string,
  ctx?: WorkflowSvcContext,
): {
  onEvent: (cb: (data: Record<string, unknown>) => void) => void;
  /**
   * Fires once, on the opening `listening` frame, with the server's own listen
   * window (`TRIGGER_LISTEN_TIMEOUT_MS`). Callers must drive their countdown
   * from this rather than mirroring the constant, so the two cannot drift.
   */
  onListening: (cb: (info: { timeoutMs: number }) => void) => void;
  onTimeout: (cb: (message: string) => void) => void;
  onError: (cb: (message: string) => void) => void;
  close: () => void;
} {
  const headers = getWorkflowServiceContextHeaders(ctx);
  // SSE via authFetch (not EventSource): manual fetch with a ReadableStream so
  // the Authorization bearer header is sent. The endpoint's authMiddleware reads
  // that header.
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/${triggerId}/listen`;

  // Held on an object rather than as four `let`s: the reader below runs inside
  // an IIFE, so TypeScript's control-flow analysis narrowed each `let` to the
  // `null` it was initialised with and reported every `cb?.()` as uncallable.
  // Property access is not narrowed that way.
  const callbacks: {
    event?: (data: Record<string, unknown>) => void;
    listening?: (info: { timeoutMs: number }) => void;
    timeout?: (message: string) => void;
    error?: (message: string) => void;
  } = {};
  let aborted = false;
  const abortController = new AbortController();

  // Start SSE fetch in background
  void (async () => {
    try {
      const response = await authFetch(url, {
        method: "GET",
        headers: { Accept: "text/event-stream", ...headers },
        signal: abortController.signal,
      });
      if (!response.ok || !response.body) {
        // Surface instead of hanging: without this the caller's "listening"
        // state never resolves on a 401/500/empty-body response.
        if (!aborted) {
          callbacks.error?.(
            `Failed to start listening (status ${response.status}). Please retry or use a sample event.`,
          );
        }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        let currentEvent = "";
        let currentData = "";

        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            currentData = line.slice(5).trim();
          } else if (line === "") {
            // End of SSE event
            if (currentEvent === "event_captured" && currentData) {
              try {
                const parsed = JSON.parse(currentData) as Record<
                  string,
                  unknown
                >;
                callbacks.event?.(parsed);
              } catch {
                // Parse error
              }
            } else if (currentEvent === "listening" && currentData) {
              try {
                const parsed = JSON.parse(currentData) as {
                  timeoutMs?: unknown;
                };
                // Only adopt a sane number — a missing or malformed field must
                // leave the caller on its own default rather than zero the timer.
                if (
                  typeof parsed.timeoutMs === "number" &&
                  Number.isFinite(parsed.timeoutMs) &&
                  parsed.timeoutMs > 0
                ) {
                  callbacks.listening?.({ timeoutMs: parsed.timeoutMs });
                }
              } catch {
                // Parse error
              }
            } else if (currentEvent === "timeout" && currentData) {
              try {
                const parsed = JSON.parse(currentData) as { message?: string };
                callbacks.timeout?.(parsed.message ?? "Timeout");
              } catch {
                callbacks.timeout?.("Timeout");
              }
            }
            currentEvent = "";
            currentData = "";
          }
        }
      }
    } catch {
      // Network error / stream drop. Ignore if intentionally closed; otherwise
      // surface so the caller isn't stuck on the listening state forever.
      if (!aborted) {
        callbacks.error?.("Connection lost while listening for an event.");
      }
    }
  })();

  return {
    onEvent: (cb) => {
      callbacks.event = cb;
    },
    onListening: (cb) => {
      callbacks.listening = cb;
    },
    onTimeout: (cb) => {
      callbacks.timeout = cb;
    },
    onError: (cb) => {
      callbacks.error = cb;
    },
    close: () => {
      aborted = true;
      abortController.abort();
    },
  };
}

/**
 * SSE endpoint: POST /api/v1/workflows/:workflowId/triggers/test-simulation
 * Streams execution events in real-time (activity-run, workflow-run, trigger-activated, error).
 * Returns a controller object with close() for cleanup.
 */
export function testTriggerSimulationStream(
  workflowId: string,
  payload: {
    triggerId: string;
    provider: string;
    eventType: string;
    catalogKey?: string;
    fieldMapping?: Record<string, string>;
    staticInputs?: Record<string, unknown>;
    connectedAccountId: string;
    capturedEvent?: Record<string, unknown>;
    executionArgs?: {
      envs?: Array<{
        name: string;
        description: string;
        value?: string | null;
      }>;
      integration_overrides?: Record<string, string>;
    };
  },
  callbacks: {
    onActivityRun?: (data: Record<string, unknown>) => void;
    onWorkflowRun?: (data: Record<string, unknown>) => void;
    onThinking?: (data: Record<string, unknown>) => void;
    onTriggerActivated?: (data: {
      runId: string | null;
      message: string;
    }) => void;
    onError?: (error: string) => void;
  },
  ctx?: WorkflowSvcContext,
): { close: () => void } {
  const headers = getWorkflowServiceContextHeaders(ctx);
  const url = `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/workflows/${workflowId}/triggers/test-simulation`;

  let aborted = false;
  const abortController = new AbortController();

  // Start SSE fetch in background
  void (async () => {
    try {
      const response = await authFetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...headers,
        },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });
      if (!response.ok || !response.body) {
        const err = (await response
          .json()
          .catch(() => ({ error: "Unknown error" }))) as { error?: string };
        callbacks.onError?.(err.error ?? `HTTP ${response.status}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        let currentEvent = "";
        let currentData = "";

        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            currentData = line.slice(5).trim();
          } else if (line === "") {
            // End of SSE event
            if (!currentEvent || !currentData || currentEvent === "ping") {
              currentEvent = "";
              currentData = "";
              continue;
            }
            const parsed = parseJsonSafe<
              Record<string, unknown> & {
                runId?: string | null;
                message?: string;
                error?: string;
              }
            >(currentData);
            if (parsed) {
              switch (currentEvent) {
                case "activity-run":
                  callbacks.onActivityRun?.(parsed);
                  break;
                case "workflow-run":
                  callbacks.onWorkflowRun?.(parsed);
                  break;
                case "thinking":
                  callbacks.onThinking?.(parsed);
                  break;
                case "trigger-activated":
                  callbacks.onTriggerActivated?.({
                    runId: parsed.runId ?? null,
                    message: parsed.message ?? "",
                  });
                  break;
                case "error":
                  callbacks.onError?.(parsed.error ?? "Unknown error");
                  break;
              }
            }
            currentEvent = "";
            currentData = "";
          }
        }
      }
    } catch {
      // Fetch aborted or network error — ignore if intentionally closed
      if (!aborted) {
        callbacks.onError?.("Connection lost during test simulation");
      }
    }
  })();

  return {
    close: () => {
      aborted = true;
      abortController.abort();
    },
  };
}

export type ResolvedUserInfo = {
  id: string;
  displayName: string;
  email?: string;
  username?: string;
};

export async function resolveUserNames(
  userIds: string[],
): Promise<Record<string, ResolvedUserInfo>> {
  const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));
  if (uniqueIds.length === 0) return {};

  try {
    const response = await authFetch("/api/v1/users/resolve", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ids: uniqueIds }),
    });

    if (!response.ok) return {};
    const data = (await response.json()) as {
      users?: Record<string, ResolvedUserInfo>;
    };
    return data.users ?? {};
  } catch (err) {
    console.warn("Failed to resolve user names:", err);
    return {};
  }
}

/**
 * Definition of one specific workflow version.
 *
 * Needed wherever the UI shows version-scoped data: reading the draft instead
 * means an older version appears to use whatever the draft currently pins,
 * which is not what that version runs.
 */
export async function getWorkflowVersionDefinition(
  workflowId: string,
  versionId: string,
  options?: WorkflowSvcContext,
) {
  const workflowServiceUrl = ENV.WORKFLOW_SVC_PUBLIC_URL;
  const { orgName, teamName, teamKey } = options ?? {};
  const response = await authFetch(
    `${workflowServiceUrl}/api/v1/workflows/${workflowId}/versions/${versionId}`,
    {
      headers: {
        ...getWorkflowServiceContextHeaders({ orgName, teamName, teamKey }),
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to load workflow version (${response.status})`);
  }
  return (await response.json()) as {
    workflow?: unknown;
    definition?: unknown;
    [key: string]: unknown;
  };
}
